// src/components/PeopleTabsSection.jsx
import React, { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  ButtonBase,
  Paper,
  Stack
} from '@mui/material';
import { typeRole } from '../../theme/typeRoles';

const TAB_KEYS = ['what', 'who', 'impact'];

const tabCopyDefault = {
  heading:
    "We’re passionate about our people – our clients, our employees, and our partners.",
  what: {
    label: 'WHAT WE DO',
    body:
      'We combine award-winning teamwork with industry-leading technology to provide a smooth and seamless loan process, working together to provide top-tier home financing to our clients.'
  },
  who: {
    label: 'WHO WE ARE',
    body:
      'We are a team of experienced mortgage professionals across the nation committed to delivering exceptional customer service and providing a personalized approach to fit our client’s needs.'
  },
  impact: {
    label: 'SOCIAL IMPACT',
    body:
      'We pride ourselves on being a valuable partner in the communities we serve by supporting local charitable organizations with both in-kind and monetary donations.'
  }
};

const TabButton = ({ active, onClick, children }) => (
  <ButtonBase
    focusRipple
    onClick={onClick}
    sx={{
      px: 4,
      py: 3,
      borderRadius: 0,
      bgcolor: active ? '#3f4a57' : '#ffffff',
      color: active ? '#fff' : '#3f4a57',
      ...typeRole('button'),
      letterSpacing: 1.2,
      borderTop: '1px solid #e7eaee',
      borderBottom: '1px solid #e7eaee',
      borderRight: '1px solid #e7eaee',
      '&:first-of-type': {
        borderLeft: '1px solid #e7eaee',
        borderTopLeftRadius: 4,
        borderBottomLeftRadius: 4
      },
      '&:last-of-type': {
        borderTopRightRadius: 4,
        borderBottomRightRadius: 4
      },
      '&:hover': {
        bgcolor: active ? '#36414d' : '#f7f9fb'
      },
      transition: 'background-color .2s ease'
    }}
  >
    {children}
  </ButtonBase>
);

/** Responsive image (16:9) with soft shadow */
const ResponsiveImage = ({ src, alt = 'People at Jet HomeLoans' }) => (
  <Box
    sx={{
      position: 'relative',
      width: '100%',
      pt: '56.25%', // 16:9
      borderRadius: 2,
      overflow: 'hidden',
      boxShadow: '0 6px 14px rgba(16,24,40,0.08), 0 2px 4px rgba(16,24,40,0.06)'
    }}
  >
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        border: 0
      }}
    />
  </Box>
);

export default function PeopleTabsSection({
  imageSrc = 'https://res.cloudinary.com/ddhrdxc7d/image/upload/v1758823978/Jet_HomeLoans_Acrylic_Standoffs_-_Photography_1_ipxxuc.jpg',
  copy = tabCopyDefault,
  containerProps
}) {
  const [active, setActive] = useState('what');

  const bodyText = useMemo(() => {
    switch (active) {
      case 'what':
        return copy.what.body;
      case 'who':
        return copy.who.body;
      case 'impact':
        return copy.impact.body;
      default:
        return '';
    }
  }, [active, copy]);

  return (
    <Box sx={{ bgcolor: '#f6f8f9' }}>
      <Container maxWidth="lg" {...containerProps} sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="flex-start">
          {/* Left: Image */}
          <Grid item xs={12} md={6}>
            <ResponsiveImage src={imageSrc} alt={copy.heading} />
          </Grid>

          {/* Right: Headline + Tabs + Body */}
          <Grid item xs={12} md={6}>
            <Typography variant="h2" sx={{ color: '#3f4a57', mb: 3 }}>
              {copy.heading}
            </Typography>

            <Paper elevation={0} sx={{ bgcolor: 'transparent', boxShadow: 'none', mb: 3 }}>
              <Stack direction="row" spacing={0} sx={{ width: '100%', overflow: 'auto', borderRadius: 1 }}>
                <TabButton active={active === 'what'} onClick={() => setActive('what')}>
                  {copy.what.label}
                </TabButton>
                <TabButton active={active === 'who'} onClick={() => setActive('who')}>
                  {copy.who.label}
                </TabButton>
                <TabButton active={active === 'impact'} onClick={() => setActive('impact')}>
                  {copy.impact.label}
                </TabButton>
              </Stack>
            </Paper>

            <Typography variant="body1" sx={{ color: '#5f6b7a' }}>
              {bodyText}
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
